import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { CSVLink } from 'react-csv'
import { FaFileCsv } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import api from 'app/AxiosInstance'
import { CLIENT_INVOICE_CSV_HEADERS } from 'app/Utils/data'
import { pushDetailKeepingList } from 'app/Utils/listQuerySync'
import NotApplicable from 'app/Components/Common/NotApplicable'
import DatePickerComponent from 'app/Components/Common/DatePicker/DatePicker'
import CustomCheckBox from 'app/Components/Common/Checkbox'

const flattenInvoiceGroups = (groups = []) =>
  groups.flatMap((group) => group?.invoices || [])

const mapInvoiceForCsv = (invoice) => ({
  invoiceNumber: invoice?.invoiceNumber || ``,
  invoiceDate: invoice?.invoiceDate || ``,
  dueDate: invoice?.dueDate || ``,
  incomeMonth: invoice?.incomeMonth || ``,
  status: invoice?.status || ``,
  statusUpdatedDate: invoice?.statusUpdatedDate || ``,
  currency: invoice?.currency || ``,
  total: invoice?.total ?? ``,
  finalAmount: invoice?.finalAmount ?? ``,
  pkrAmount: invoice?.pkrAmount ?? ``,
  usdAmount: invoice?.usdAmount ?? ``,
  projectName: invoice?.projectId?.name || ``,
  paymentMode: invoice?.transaction?.paymentMode || ``,
  accountTitle: invoice?.transaction?.accountTitle || ``,
  bankName: invoice?.transaction?.bankName || ``,
  branchCode: invoice?.transaction?.branchCode || ``,
  accountNumber: invoice?.transaction?.accountNumber || ``,
  ibanNumber: invoice?.transaction?.ibanNumber || ``,
  accountEmail: invoice?.transaction?.accountEmail || ``,
})

const statusColor = (status) => {
  if (status === `PAID`) return { bg: `rgba(77, 198, 64, 0.12)`, color: `#4DC640` }
  if (status === `UNPAID`) return { bg: `rgba(249, 171, 6, 0.12)`, color: `#F9AB06` }
  if (status === `CANCELLED`) return { bg: `rgba(255, 72, 94, 0.1)`, color: `#FF485E` }
  return { bg: `rgba(40, 32, 61, 0.08)`, color: `#28203D` }
}

const currentYear = new Date().getFullYear()
const defaultStart = `${currentYear}-01-01`
const defaultEnd = `${currentYear}-12-31`

const MONTH_SHORT = [
  `Jan`,
  `Feb`,
  `Mar`,
  `Apr`,
  `May`,
  `Jun`,
  `Jul`,
  `Aug`,
  `Sep`,
  `Oct`,
  `Nov`,
  `Dec`,
]

/** `2026-01-01` → `01-Jan-2026` for readable export filenames */
const fileDateLabel = (iso) => {
  if (!iso || typeof iso !== `string`) return iso || `date`
  const [year, month, day] = iso.split(`-`)
  const name = MONTH_SHORT[Number(month) - 1]
  if (!name || !year || !day) return iso
  return `${day}-${name}-${year}`
}

/**
 * Reusable client invoice list + CSV export (opens in Excel).
 * Date range + status flags filter list + export via GET /invoices.
 */
const ClientInvoicesSection = ({ clientId, clientName = `client` }) => {
  const history = useHistory()
  const location = useLocation()
  const colors = useSelector((state) => state.colors)
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [paid, setPaid] = useState(true)
  const [unpaid, setUnpaid] = useState(true)
  const [cancelled, setCancelled] = useState(true)

  const rangeInvalid = Boolean(
    startDate && endDate && String(startDate) > String(endDate),
  )
  const statusInvalid = !paid && !unpaid && !cancelled

  const { data: invoiceGroups, isLoading, isFetching } = useQuery(
    [`clientInvoices`, clientId, startDate, endDate, paid, unpaid, cancelled],
    async () => {
      const params = new URLSearchParams({
        clientId: String(clientId),
        paid: String(paid),
        unpaid: String(unpaid),
        cancelled: String(cancelled),
      })
      if (startDate) params.set(`startDate`, startDate)
      if (endDate) params.set(`endDate`, endDate)
      const { data } = await api.get(`/invoices?${params.toString()}`)
      return data?.data || []
    },
    {
      enabled: Boolean(clientId) && !rangeInvalid && !statusInvalid,
      refetchOnWindowFocus: false,
      cacheTime: 1,
    },
  )

  const invoices = useMemo(
    () => flattenInvoiceGroups(invoiceGroups),
    [invoiceGroups],
  )

  const csvData = useMemo(
    () => invoices.map(mapInvoiceForCsv),
    [invoices],
  )

  const safeName =
    String(clientName || `client`)
      .replace(/[^a-z0-9-_]+/gi, `-`)
      .replace(/^-|-$/g, ``)
      .toLowerCase() || `client`

  const rangeSuffix =
    startDate || endDate
      ? `_${fileDateLabel(startDate || `start`)}_${fileDateLabel(
          endDate || `end`,
        )}`
      : ``
  const statusSuffix = [
    paid ? `paid` : null,
    unpaid ? `unpaid` : null,
    cancelled ? `cancelled` : null,
  ]
    .filter(Boolean)
    .join(`-`)
  const statusSuffixPart = statusSuffix ? `_${statusSuffix}` : ``

  return (
    <Box sx={{ marginTop: `20px` }}>
      <Box
        sx={{
          display: `flex`,
          justifyContent: `space-between`,
          alignItems: `center`,
          marginBottom: `10px`,
          gap: `8px`,
        }}
      >
        <Typography
          sx={{
            fontFamily: `Inter`,
            fontWeight: 600,
            fontSize: `14px`,
            color: `#28203D`,
          }}
        >
          Invoices & Payments
        </Typography>

        {csvData.length > 0 && !rangeInvalid && !statusInvalid && (
          <CSVLink
            data={csvData}
            headers={CLIENT_INVOICE_CSV_HEADERS}
            filename={`${safeName}-invoices${rangeSuffix}${statusSuffixPart}.csv`}
            style={{
              display: `inline-flex`,
              alignItems: `center`,
              gap: `6px`,
              textDecoration: `none`,
              padding: `4px 10px`,
              borderRadius: `8px`,
              backgroundColor: colors?.appColor || `#7D4DEC`,
              color: `#fff`,
              fontFamily: `Inter`,
              fontSize: `12px`,
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            <FaFileCsv size={14} />
            Export
          </CSVLink>
        )}
      </Box>

      <Box
        sx={{
          display: `flex`,
          flexDirection: `column`,
          gap: `12px`,
          marginBottom: `12px`,
        }}
      >
        <DatePickerComponent
          label={`Start Date`}
          bgcolor={`#FFF`}
          value={startDate}
          onChange={setStartDate}
          width={`100%`}
          height={`40px`}
          border={`1px solid rgba(40, 32, 61, 0.15)`}
        />
        <DatePickerComponent
          label={`End Date`}
          bgcolor={`#FFF`}
          value={endDate}
          onChange={setEndDate}
          minDate={startDate || undefined}
          width={`100%`}
          height={`40px`}
          border={`1px solid rgba(40, 32, 61, 0.15)`}
        />
      </Box>

      <Box
        sx={{
          display: `flex`,
          flexWrap: `wrap`,
          alignItems: `center`,
          columnGap: `4px`,
          rowGap: `0`,
          marginBottom: `12px`,
          '& .MuiFormControlLabel-root': {
            marginLeft: 0,
            marginRight: `12px`,
          },
          '& .MuiCheckbox-root': {
            padding: `6px`,
          },
          '& .MuiFormControlLabel-label': {
            fontFamily: `Inter`,
            fontSize: `13px`,
          },
        }}
      >
        <CustomCheckBox
          checked={paid}
          onChange={(e) => setPaid(e.target.checked)}
          label="Paid"
        />
        <CustomCheckBox
          checked={unpaid}
          onChange={(e) => setUnpaid(e.target.checked)}
          label="Unpaid"
        />
        <CustomCheckBox
          checked={cancelled}
          onChange={(e) => setCancelled(e.target.checked)}
          label="Cancelled"
        />
      </Box>

      {rangeInvalid ? (
        <Typography
          sx={{
            fontFamily: `Inter`,
            fontSize: `12px`,
            color: `#FF485E`,
            marginBottom: `8px`,
          }}
        >
          Start date must be before end date
        </Typography>
      ) : null}

      {statusInvalid ? (
        <Typography
          sx={{
            fontFamily: `Inter`,
            fontSize: `12px`,
            color: `#FF485E`,
            marginBottom: `8px`,
          }}
        >
          Select at least one status
        </Typography>
      ) : null}

      {statusInvalid ? null : isLoading || isFetching ? (
        <Typography
          sx={{ fontFamily: `Inter`, fontSize: `13px`, opacity: 0.7 }}
        >
          Loading invoices…
        </Typography>
      ) : invoices.length === 0 ? (
        NotApplicable
      ) : (
        <Box sx={{ display: `flex`, flexDirection: `column`, gap: `8px` }}>
          {invoices.map((invoice) => {
            const badge = statusColor(invoice?.status)
            return (
              <Box
                key={invoice?._id}
                onClick={() => {
                  const params = new URLSearchParams(location.search || ``)
                  if (clientId) params.set(`id`, String(clientId))
                  const qs = params.toString()
                  pushDetailKeepingList(
                    history,
                    {
                      ...location,
                      pathname: `/clients`,
                      search: qs ? `?${qs}` : ``,
                    },
                    `/income/${invoice._id}`,
                  )
                }}
                sx={{
                  display: `flex`,
                  flexDirection: `column`,
                  gap: `4px`,
                  padding: `10px`,
                  borderRadius: `8px`,
                  border: `1px solid rgba(40, 32, 61, 0.08)`,
                  cursor: `pointer`,
                  '&:hover': { backgroundColor: `rgba(125, 77, 236, 0.04)` },
                }}
              >
                <Box
                  sx={{
                    display: `flex`,
                    justifyContent: `space-between`,
                    alignItems: `center`,
                    gap: `8px`,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: `Inter`,
                      fontWeight: 600,
                      fontSize: `13px`,
                      color: `#28203D`,
                    }}
                  >
                    {invoice?.invoiceNumber || `—`}
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: `20px`,
                      paddingY: `2px`,
                      paddingX: `8px`,
                      backgroundColor: badge.bg,
                      color: badge.color,
                      fontFamily: `Inter`,
                      fontSize: `11px`,
                      fontWeight: 500,
                    }}
                  >
                    {invoice?.status}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontFamily: `Inter`,
                    fontSize: `12px`,
                    color: `#28203D`,
                    opacity: 0.7,
                  }}
                >
                  {invoice?.invoiceDate || `—`}
                  {invoice?.projectId?.name
                    ? ` · ${invoice.projectId.name}`
                    : ``}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: `Inter`,
                    fontSize: `12px`,
                    fontWeight: 500,
                    color: `#28203D`,
                  }}
                >
                  {invoice?.pkrAmount != null
                    ? `${invoice.pkrAmount} PKR`
                    : `—`}
                  {invoice?.usdAmount != null
                    ? ` · ${invoice.usdAmount} USD`
                    : ``}
                </Typography>
                {invoice?.transaction?.paymentMode && (
                  <Typography
                    sx={{
                      fontFamily: `Inter`,
                      fontSize: `11px`,
                      color: `#28203D`,
                      opacity: 0.65,
                    }}
                  >
                    {invoice.transaction.paymentMode}
                    {invoice.transaction.bankName
                      ? ` · ${invoice.transaction.bankName}`
                      : ``}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default ClientInvoicesSection
